import { Logout } from 'components/logout/Logout';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import * as React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import Error404 from 'screens/errors/404/Error404';
import { getUserAccountType } from '../helpers/user/getUserAccountType';
import { LqaUserAccessType } from '../enums/LqaUserAccessType';
import { isOnAdminSide } from '../helpers/user/isOnAdminSide';
import { getUserData } from '../helpers/user/getUserData';
import { getFlag } from '../helpers/featureFlags/featureFlags';
import { MicroApp } from '../modules/MicroApp';
// import TrainingFeedback from 'screens/upfrontTraining/TrainingFeedback';

// Lazy load screens
const TrainingFeedback = React.lazy(() => import('screens/upfrontTraining/TrainingFeedback'));
const Assessments = React.lazy(() => import('screens/assessments/Assessments'));
const PublishedAssessments = React.lazy(() => import('screens/publishedAssessments/PublishedAssessments'));
const AddAssessment = React.lazy(() => import('screens/assessments/addAssessment/AddAssessment'));
const Page = React.lazy(() => import('screens/page/Page'));
const EditAssessment = React.lazy(() => import('screens/assessments/editAssessment/EditAssessment'));
const Brands = React.lazy(() => import('screens/brand/Brands'));
const AddBrand = React.lazy(() => import('screens/brand/AddBrand'));
const EditBrand = React.lazy(() => import('screens/brand/EditBrand'));
const AddHotel = React.lazy(() => import('screens/hotel/AddHotel'));
const EditHotel = React.lazy(() => import('screens/hotel/EditHotel'));
const Hotels = React.lazy(() => import('screens/hotel/Hotels'));
const EditClient = React.lazy(() => import('screens/client/EditClient'));
const AddClient = React.lazy(() => import('screens/client/AddClient'));
const Clients = React.lazy(() => import('screens/client/Clients'));
const LqaUsers = React.lazy(() => import('screens/lqaUser/LqaUsers'));
const AddLqaUser = React.lazy(() => import('screens/lqaUser/AddLqaUser'));
const EditLqaUser = React.lazy(() => import('screens/lqaUser/EditLqaUser'));
const Questionnaire = React.lazy(() => import('screens/questionnaire/admin/Questionnaire'));
const Resources = React.lazy(() => import('screens/resources/admin/Resources'));
const ViewResources = React.lazy(() => import('screens/resources/admin/Resources'));
const QuestionnaireReport = React.lazy(() => import('screens/questionnaire/admin/QuestionnaireReport'));
const QuestionnaireSummary = React.lazy(() => import('screens/questionnaire/admin/QuestionnaireSummary'));
const QuestionnaireSummaryHotelReport = React.lazy(
  () => import('screens/questionnaire/admin/QuestionnaireHotelReport')
);
const BookingReport = React.lazy(() => import('screens/booking/admin/BookingReport'));
const AddQuestion = React.lazy(() => import('screens/questionnaire/admin/AddQuestionnaire'));
const EditQuestion = React.lazy(() => import('screens/questionnaire/admin/EditQuestionnaire'));
const AddRegion = React.lazy(() => import('screens/region/AddRegion'));
const EditRegion = React.lazy(() => import('screens/region/EditRegion'));
const Locations = React.lazy(() => import('screens/location/Locations'));
const Regions = React.lazy(() => import('screens/region/Regions'));
const EditClientUser = React.lazy(() => import('screens/clientUser/admin/EditClientUser'));
const AddClientUser = React.lazy(() => import('screens/clientUser/admin/AddClientUser'));
const ClientUsers = React.lazy(() => import('screens/clientUser/admin/ClientUsers'));
const Categories = React.lazy(() => import('screens/category/Categories'));
const AddCategory = React.lazy(() => import('screens/category/AddCategory'));
const EditCategory = React.lazy(() => import('screens/category/EditCategory'));
const Standards = React.lazy(() => import('screens/standard/Standards'));
const AddStandard = React.lazy(() => import('screens/standard/AddStandard'));
const EditStandard = React.lazy(() => import('screens/standard/EditStandard'));
const CustomCompSets = React.lazy(() => import('screens/customCompSet/CustomCompSets'));
const AddCustomCompSet = React.lazy(() => import('screens/customCompSet/AddCustomCompSet'));
const EditCustomCompSet = React.lazy(() => import('screens/customCompSet/EditCustomCompSet'));
const ManageCompSets = React.lazy(() => import('screens/compSet/ManageCompSets'));
const Blackouts = React.lazy(() => import('screens/blackoutDate/admin/BlackoutDates'));
const AddBlackout = React.lazy(() => import('screens/blackoutDate/admin/AddBlackoutDate'));
const EditBlackout = React.lazy(() => import('screens/blackoutDate/admin/EditBlackoutDate'));
const Account = React.lazy(() => import('screens/account/Account'));
const AddTemplate = React.lazy(() => import('screens/template/addTemplate/AddTemplate'));
const EditTemplate = React.lazy(() => import('screens/template/editTemplate/EditTemplate'));
const ViewTemplate = React.lazy(() => import('screens/template/viewTemplate/ViewTemplate'));
const Templates = React.lazy(() => import('screens/template/Templates'));
const EditUpfrontTraining = React.lazy(() => import('screens/upfrontTraining/EditUpfrontTraining'));
const SpottedReport = React.lazy(() => import('screens/spottedReport/SpottedReport'));
const Represent = React.lazy(() => import('screens/clientUser/RepresentApp'));
const ContractManagement = React.lazy(() => import('screens/contracting/Contracting'));
const AssessmentManagement = React.lazy(() => import('screens/contracting/assessmentManagement/AssessmentManagement'));
const NewsFeed = React.lazy(() => import('screens/newsFeed/admin/NewsFeed'));
const AddNewsFeed = React.lazy(() => import('screens/newsFeed/admin/AddNewsFeed'));
const EditNewsFeed = React.lazy(() => import('screens/newsFeed/admin/EditNewsFeed'));
const HotelScheduling = React.lazy(() => import('screens/scheduling/HotelScheduling'));
const StaffSchedulingReport = React.lazy(() => import('screens/scheduling/staffScheduling/StaffSchedulingReport'));
const AdditionalScheduling = React.lazy(() => import('screens/scheduling/additionalScheduling/AdditionalScheduling'));
const Roster = React.lazy(() => import('screens/scheduling/roster/Roster'));
const ManageRoster = React.lazy(() => import('screens/scheduling/roster/admin/ManageRoster'));
const SpecialRequests = React.lazy(() => import('screens/scheduling/specialRequests/SpecialRequests'));
const HolidayRequests = React.lazy(() => import('screens/scheduling/holidayRequests/HolidayRequests'));
const PartnerAvailability = React.lazy(() => import('screens/scheduling/partnerAvailability/PartnerAvailability'));
const OfficeWeekManager = React.lazy(() => import('screens/scheduling/officeWeekManager/OfficeWeekManager'));
const FlightPlanner = React.lazy(() => import('screens/scheduling/flightPlanner/FlightPlannerList'));
const AddNewFlightPlanner = React.lazy(() => import('screens/scheduling/flightPlanner/AddFlightPlanner'));
const ViewFlightPlanner = React.lazy(() => import('screens/scheduling/flightPlanner/ViewFlightPlanner'));
const EditFlightPlanner = React.lazy(() => import('screens/scheduling/flightPlanner/EditFlightPlanner'));
const HolidayLocationMain = React.lazy(() => import('screens/holidayLocation/admin/HolidayLocationMain'));
const AddHolidayLocation = React.lazy(() => import('screens/holidayLocation/admin/AddHolidayLocation'));
const EditHolidayLocation = React.lazy(() => import('screens/holidayLocation/admin/EditHolidayLocation'));
const HolidayLocation = React.lazy(() => import('screens/holidayLocation/admin/HolidayLocation'));
const ManageRequests = React.lazy(() => import('screens/scheduling/manageRequests/ManageRequests'));
const DaysManager = React.lazy(() => import('screens/scheduling/daysManager/DaysManager'));
const HolidayRequestsAdmin = React.lazy(() => import('screens/scheduling/holidayRequests/admin/ManageHolidayRequests'));
const SpecialRequestsAdmin = React.lazy(() => import('screens/scheduling/specialRequests/admin/ManageSpecialRequests'));
const MyLqaSubscriptionSummary = React.lazy(() => import('screens/myLqaSubscriptionSummary/MyLqaSubscriptionSummary'));
const WeeklyBoosterCategories = React.lazy(() => import('screens/weeklyBooster/admin/WeeklyBoosterCategories'));
const WeeklyBoosterCategoryForm = React.lazy(() => import('screens/weeklyBooster/admin/WeeklyBoosterCategoryForm'));
const WeeklyBoosters = React.lazy(() => import('screens/weeklyBooster/admin/WeeklyBoosters'));
const WeeklyBoosterForm = React.lazy(() => import('screens/weeklyBooster/admin/WeeklyBoosterForm'));

const isUserAllowedToTemplates = (userData) => {
  return getUserAccountType(userData) === LqaUserAccessType.LQAAdmin;
};

const AdminRouter = () => {
  const onAdminSide = isOnAdminSide(useLocation());
  const userData = getUserData(onAdminSide);
  const isNewAdminRosterEnabled = getFlag('isNewAdminRosterEnabled');

  return (
    <React.Suspense fallback={<LoadingContent layout="absolute" />}>
      <Switch>
        {/* TODO: locale setup step 2: add /:locale before the paths */}
        <Route path="/admin/login" render={() => <Redirect to="/admin/clients/clients" />} />
        <Route path="/admin/management/spotted-report" component={SpottedReport} />
        <Route path="/admin/clients/clients/edit-client/:id" component={EditClient} />
        <Route path="/admin/clients/clients/add-client" component={AddClient} />
        <Route path="/admin/clients/clients" exact={true} component={Clients} />
        <Route path="/admin/clients/brands/add-brand" component={AddBrand} />
        <Route path="/admin/clients/brands/edit-brand/:id" component={EditBrand} />
        <Route path="/admin/clients/brands/view-brand/:id" component={EditBrand} />
        <Route path="/admin/upfront-training/hotels/edit-hotel/:id" component={EditUpfrontTraining} />
        <Route path="/admin/upfront-training/hotels/view-hotel/:id" component={EditUpfrontTraining} />
        <Route path="/admin/clients/brands/view-brand/:id" component={EditUpfrontTraining} />
        <Route path="/admin/clients/brands" exact={true} component={Brands} />
        <Route path="/admin/clients/hotels/add-hotel" component={AddHotel} />
        <Route path="/admin/clients/hotels/edit-hotel/:id" component={EditHotel} />
        <Route path="/admin/clients/hotels/view-hotel/:id" component={EditHotel} />
        <Route path="/admin/clients/hotels" exact={true} component={Hotels} />
        <Route path="/admin/upfront-training/hotels" exact={true} component={Hotels} />
        <Route path="/admin/upfront-training/feedback" exact={true} component={TrainingFeedback} />
        <Route path="/admin/management/lqa-users/add-lqa-user" component={AddLqaUser} />
        <Route path="/admin/management/lqa-users/edit-lqa-user/:id" component={EditLqaUser} />
        <Route path="/admin/management/lqa-users" exact={true} component={LqaUsers} />
        <Route path="/admin/management/mylqa-subscriptions-summary" component={MyLqaSubscriptionSummary} />
        <Route path="/admin/management/news-feed" exact={true} component={NewsFeed} />
        <Route path="/admin/management/news-feed/add" exact={true} component={AddNewsFeed} />
        <Route path="/admin/management/news-feed/edit/:id" exact={true} component={EditNewsFeed} />
        <Route
          path="/admin/management/weekly-boosters/categories/add"
          exact={true}
          component={WeeklyBoosterCategoryForm}
        />
        <Route
          path="/admin/management/weekly-boosters/categories/edit/:id"
          exact={true}
          component={WeeklyBoosterCategoryForm}
        />
        <Route path="/admin/management/weekly-boosters/categories" exact={true} component={WeeklyBoosterCategories} />
        <Route path="/admin/management/weekly-boosters/boosters/add" exact={true} component={WeeklyBoosterForm} />
        <Route path="/admin/management/weekly-boosters/boosters/edit/:id" exact={true} component={WeeklyBoosterForm} />
        <Route path="/admin/management/weekly-boosters/boosters" exact={true} component={WeeklyBoosters} />
        <Route path="/admin/clients/custom-comp-sets/edit-custom-comp-set/:id" component={EditCustomCompSet} />
        <Route path="/admin/clients/custom-comp-sets/add-custom-comp-set" component={AddCustomCompSet} />
        <Route path="/admin/clients/custom-comp-sets" exact={true} component={CustomCompSets} />
        <Route path="/admin/clients/comp-sets" exact={true} component={ManageCompSets} />
        <Route path="/admin/clients/custom-regions/add" component={AddRegion} />
        <Route path="/admin/clients/locations">
          <Locations />
        </Route>
        <Route path="/admin/clients/custom-regions/edit/:id">
          <EditRegion />
        </Route>
        <Route path="/admin/clients/custom-regions" exact={true}>
          <Regions />
        </Route>
        <Route path="/admin/clients/users/edit-client-user/:id" component={EditClientUser} />
        <Route path="/admin/clients/users/add-client-user" component={AddClientUser} />
        <Route path="/admin/clients/users" exact={true} component={ClientUsers} />
        <Route path="/admin/management/questionnaire/add-question" component={AddQuestion} />
        <Route path="/admin/management/questionnaire/edit-question/:id" component={EditQuestion} />
        <Route path="/admin/management/questionnaire" exact={true} component={Questionnaire} />
        <Route path="/admin/management/resources" exact={true} component={Resources} />
        <Route path="/admin/management/view-resources" exact={true} component={ViewResources} />
        <Route path="/admin/management/questionnaire-report" component={QuestionnaireReport} />
        <Route path="/admin/management/training-questionnaire-report" component={QuestionnaireReport} />
        <Route
          path="/admin/management/questionnaire-summary/hotel-report/:hotelId/:hotelName"
          component={QuestionnaireSummaryHotelReport}
        />
        <Route
          path="/admin/management/training-questionnaire-summary/hotel-report/:hotelId/:hotelName"
          component={QuestionnaireSummaryHotelReport}
        />
        <Route path="/admin/management/questionnaire-summary" component={QuestionnaireSummary} />
        <Route path="/admin/management/training-questionnaire-summary" component={QuestionnaireSummary} />
        <Route path="/admin/management/booking-report" component={BookingReport} />
        <Route path="/admin/assessments/standards/add-standard" component={AddStandard} />
        <Route path="/admin/assessments/standards/edit-standard/:id" component={EditStandard} />
        <Route path="/admin/assessments/standards" exact={true} component={Standards} />
        <Route path="/admin/assessments/standards" component={Standards} />
        <Route path="/admin/assessments/categories/add-category" component={AddCategory} />
        <Route path="/admin/assessments/categories/edit-category/:id" component={EditCategory} />
        <Route path="/admin/assessments/categories" exact={true} component={Categories} />
        <Route path="/admin/assessments/categories" component={Categories} />

        <Route path="/admin/management/blackouts/add-blackout" component={AddBlackout} />
        <Route path="/admin/management/blackouts/edit-blackout/:id" component={EditBlackout} />
        <Route path="/admin/management/blackouts" exact={true} component={Blackouts} />
        {Env.BO_FEATURE_FLAG_ENABLED === 'no' &&
          userData.isSchedulePageVisible && [
            <Route
              path="/admin/management/holiday-location-main"
              exact={true}
              component={HolidayLocationMain}
              key={0}
            />,
            <Route path="/admin/management/holiday-location/:id" exact={true} component={HolidayLocation} key={1} />,
            <Route
              path="/admin/management/add-holiday-location/:id/:location"
              exact={true}
              component={AddHolidayLocation}
              key={2}
            />,
            <Route
              path="/admin/management/edit-holiday-location"
              exact={true}
              component={EditHolidayLocation}
              key={3}
            />,
          ]}
        <Route
          path="/admin/assessments/assessments/edit-assessment/:id/:tabID?/:departmentId?"
          component={EditAssessment}
        />
        <Route
          path="/admin/assessments/assessments/view-assessment/:id/:tabID?/:departmentId?"
          component={EditAssessment}
        />
        <Route path="/admin/assessments/assessments/add-assessment" component={AddAssessment} />
        <Route path="/admin/assessments/assessments" component={Assessments} />
        <Route path="/admin/assessments/published-assessments" component={PublishedAssessments} />
        <Route path="/admin/represent/user" component={Represent} />
        {Env.BO_FEATURE_FLAG_ENABLED === 'no' &&
          userData.isSchedulePageVisible && [
            <Route path="/admin/contracting/contract-management" component={ContractManagement} key={0} />,
            <Route
              path="/admin/contracting/assessment-management/:contractId"
              component={AssessmentManagement}
              key={1}
            />,
            <Route path="/admin/contracting/assessment-management" component={AssessmentManagement} key={2} />,
          ]}

        {Env.BO_FEATURE_FLAG_ENABLED === 'no' &&
          userData.isSchedulePageVisible && [
            <Route path="/admin/scheduling/hotel-scheduling" component={HotelScheduling} key={0} />,
            <Route path="/admin/scheduling/roster" component={Roster} key={2} />,
            <Route path="/admin/scheduling/manage-roster" component={ManageRoster} key={3} />,
            ...(isNewAdminRosterEnabled
              ? [
                  <Route
                    path="/admin/scheduling/new-roster"
                    render={() => <MicroApp url={Env.MICRO_APP_NEW_ADMIN_ROSTER_URL} />}
                    key={31}
                  />,
                ]
              : []),
            <Route
              path="/admin/scheduling/special-requests/view/:id/:userId"
              component={SpecialRequestsAdmin}
              key={3}
            />,
            <Route
              path="/admin/scheduling/special-requests/edit/:id/:userId"
              component={SpecialRequestsAdmin}
              key={4}
            />,
            <Route path="/admin/scheduling/special-requests" component={SpecialRequests} key={5} />,
            <Route path="/admin/scheduling/manage-requests" component={ManageRequests} key={6} />,
            <Route path="/admin/scheduling/days-manager" component={DaysManager} key={7} />,
            <Route
              path="/admin/scheduling/holiday-requests/view/:id/:userId"
              component={HolidayRequestsAdmin}
              key={8}
            />,
            <Route
              path="/admin/scheduling/holiday-requests/edit/:id/:userId"
              component={HolidayRequestsAdmin}
              key={9}
            />,
            <Route path="/admin/scheduling/holiday-requests" component={HolidayRequests} key={10} />,
            <Route path="/admin/scheduling/partner-availability" component={PartnerAvailability} key={11} />,
            <Route path="/admin/scheduling/office-week-manager" component={OfficeWeekManager} key={12} />,
            <Route path="/admin/scheduling/flight-planner/add" component={AddNewFlightPlanner} key={13} />,
            <Route
              path="/admin/scheduling/flight-planner/edit/:id/:month/:year"
              component={EditFlightPlanner}
              key={14}
            />,
            <Route path="/admin/scheduling/flight-planner/view/:id" component={ViewFlightPlanner} key={15} />,
            <Route path="/admin/scheduling/flight-planner" component={FlightPlanner} key={16} />,
            <Route path="/admin/scheduling/staff-scheduling" component={StaffSchedulingReport} key={17} />,
            <Route path="/admin/scheduling/additional-scheduling" component={AdditionalScheduling} key={18} />,
          ]}
        {isUserAllowedToTemplates(userData) && [
          <Route path="/admin/assessments/templates/add-template" component={AddTemplate} key={1} />,
          <Route
            path="/admin/assessments/templates/edit-template/:id/:tabID(standards)?/:departmentId?"
            component={EditTemplate}
            key={2}
          />,
          <Route
            path="/admin/assessments/templates/view-template/:id/:tabID(standards)?/:departmentId?"
            component={ViewTemplate}
            key={3}
          />,
          <Route
            path="/admin/assessments/active-template"
            exact={true}
            render={() => <Redirect to="/admin/assessments/templates?search=%7B%22isActive%22%3Atrue%7D" />}
            key={4}
          />,
          <Route path="/admin/assessments/templates" exact={true} component={Templates} key={5} />,
        ]}
        <Route path="/admin/my-account" exact={true} component={Account} />
        <Route strict={true} path="/admin/page/:path+/" exact={true} component={Page} />
        <Route strict={true} path="/admin/page/:path*/:page" exact={true} component={Page} />
        <Route path="/admin" exact={true} render={() => <Redirect to="/admin/clients/clients" />} />
        <Route path="/admin/logout" exact={true} component={Logout} />
        <Route path="/admin" component={Error404} />
      </Switch>
    </React.Suspense>
  );
};

export default AdminRouter;
